/**
 * Typed API client. Keep types here in sync with the server's Zod schemas.
 *
 * Why a thin wrapper instead of fetch() everywhere?
 *   - One place to inject auth headers / base URL / error handling
 *   - Callers get a typed surface (api.items.list()) rather than URL strings
 *   - Easy to swap for a generated client (OpenAPI/tRPC) later
 */

const BASE_URL = import.meta.env.VITE_API_BASE_URL ?? 'http://localhost:8787';

export interface Item {
  id: number;
  name: string;
  description: string | null;
  category: string;
  status: string;
  created_at: string;
  updated_at: string;
}

export interface ListItemsParams {
  category?: string;
  status?: string;
  limit?: number;
  offset?: number;
}

export interface CategoryCount {
  category: string;
  count: number;
}

/** Shape of error responses from the API. */
export interface ApiErrorBody {
  error: { code: string; message: string; details?: unknown; requestId: string };
}

/** Thrown by the client when the API returns a non-2xx response. */
export class ApiClientError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    message: string,
    public readonly details?: unknown,
  ) {
    super(message);
  }
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const res = await fetch(`${BASE_URL}${path}`, {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      ...(init.headers ?? {}),
    },
  });

  if (res.status === 204) return undefined as T;

  let body: unknown;
  try {
    body = await res.json();
  } catch {
    throw new ApiClientError(res.status, 'invalid_response', 'Server returned non-JSON');
  }

  if (!res.ok) {
    const err = (body as ApiErrorBody).error;
    throw new ApiClientError(res.status, err?.code ?? 'unknown', err?.message ?? 'Request failed', err?.details);
  }
  return body as T;
}

/**
 * Build a query string, skipping undefined/null. Saves boilerplate at call sites.
 */
function qs(params: object): string {
  const entries = (Object.entries(params) as [string, string | number | undefined | null][]).filter(
    ([, v]) => v !== undefined && v !== null && v !== '',
  );
  if (entries.length === 0) return '';
  return '?' + new URLSearchParams(entries.map(([k, v]) => [k, String(v)])).toString();
}

export const api = {
  health: () => request<{ status: string; time: string }>('/health'),

  items: {
    list: (params: ListItemsParams = {}) =>
      request<{ data: Item[]; limit: number; offset: number }>(`/items${qs(params)}`),
    get: (id: number) => request<Item>(`/items/${id}`),
    create: (input: Omit<Item, 'id' | 'created_at' | 'updated_at'>, token: string) =>
      request<Item>('/items', {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: JSON.stringify(input),
      }),
    update: (id: number, input: Partial<Omit<Item, 'id' | 'created_at' | 'updated_at'>>, token: string) =>
      request<Item>(`/items/${id}`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${token}` },
        body: JSON.stringify(input),
      }),
    delete: (id: number, token: string) =>
      request<void>(`/items/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      }),
  },

  categories: {
    list: () => request<{ data: CategoryCount[] }>('/categories'),
  },
};
